var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createStyles, HawkinsGlobalClass, makeStyles } from '@hawkins/styles';
export var COMPACT_BUTTON_MIN_HEIGHT_SIZING_KEY = 'size2';
export var BUTTON_MIN_HEIGHT_SIZING_KEY = 'size4';
export var COMPACT_ICON_BUTTON_ICON_DIM_SIZING_KEY = 'size1';
export var ICON_BUTTON_ICON_DIM_SIZING_KEY = 'size2';
var iconChildrenSelector = '& $caret, & $icon, & $loader';
export function buttonStyles(theme) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    var hawkins = theme.hawkins, hawkinsUtils = theme.hawkinsUtils, hawkinsVariables = theme.hawkinsVariables;
    var borderWidth = parseInt(hawkins.ui.borderWidthRegular, 10);
    var columnGap = hawkinsVariables.spacings.space0;
    var focusRingBorderRadius = hawkinsVariables.borderRadiusValues.borderRadius0 * 2;
    var minHeight = hawkinsVariables.sizingValues[BUTTON_MIN_HEIGHT_SIZING_KEY];
    var minHeightCompact = hawkinsVariables.sizingValues[COMPACT_BUTTON_MIN_HEIGHT_SIZING_KEY];
    return createStyles({
        /** Class name of the caret icon. */
        caret: {
            color: hawkins.icon.fillRegular,
            pointerEvents: 'none',
        },
        /** Class name of the content container when the component is in its executing state. */
        executingContentContainer: {
            columnGap: columnGap,
            // Needs to be a flex box (same as `.root`) to have the correct height.
            display: 'flex',
        },
        /** Class name of the icon. */
        icon: {
            color: hawkins.icon.fillRegular,
            pointerEvents: 'none',
        },
        /** Class name of the loader when the component is in its executing state. */
        loader: {
            color: hawkins.icon.fillRegular,
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
        },
        /** Class name of the root element. */
        root: (_a = {
                alignItems: 'center',
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                borderStyle: 'solid',
                borderWidth: borderWidth,
                borderRadius: hawkinsVariables.borderRadius.borderRadius0,
                color: hawkins.textColor.textColorRegular,
                columnGap: columnGap,
                cursor: 'pointer',
                display: 'inline-flex',
                fontFamily: hawkinsVariables.fontFamily.fontFamilySans,
                fontSize: hawkins.body.fontSize0,
                fontWeight: hawkins.body.fontWeightBold,
                justifyContent: 'center',
                lineHeight: hawkins.body.lineHeightRegular,
                minHeight: minHeight,
                padding: "".concat((hawkinsVariables.spacingValues.space2 * 5) / 6 - borderWidth, "px ").concat(hawkinsVariables.spacings.space2),
                position: 'relative',
                // In case the component is being used as a link.
                textDecoration: 'none'
            },
            _a["&.".concat(HawkinsGlobalClass.FOCUSABLE)] = (_b = {},
                _b[hawkinsUtils.getFocusRingedElementSelector('&', '::after')] = __assign(__assign({}, hawkinsUtils.getFocusRingPosition(2, 1)), { borderRadius: focusRingBorderRadius }),
                _b),
            _a['&[aria-expanded="true"]'] = { backgroundColor: hawkins.state.backgroundColorHover },
            _a),
        /** Class name of the root element when the component is compact. */
        rootCompact: (_c = {
                fontSize: hawkins.label.fontSize0,
                fontWeight: hawkins.label.fontWeightBold,
                lineHeight: hawkins.label.lineHeightRegular,
                minHeight: minHeightCompact,
                padding: "".concat(hawkinsVariables.spacingValues.space0 - borderWidth, "px ").concat(hawkinsVariables.spacings.space1)
            },
            _c["&.".concat(HawkinsGlobalClass.FOCUSABLE)] = (_d = {},
                _d[hawkinsUtils.getFocusRingedElementSelector('&', '::after')] = { borderRadius: focusRingBorderRadius - 1 },
                _d),
            _c),
        /** Class name of the root element when the component is an icon button. */
        rootIconButton: {
            minHeight: 'auto',
            padding: (minHeight - hawkinsVariables.sizingValues[ICON_BUTTON_ICON_DIM_SIZING_KEY]) / 2 - borderWidth,
        },
        /** Class name of the root element when the component is a compact icon button. */
        rootIconButtonCompact: {
            padding: (minHeightCompact - hawkinsVariables.sizingValues[COMPACT_ICON_BUTTON_ICON_DIM_SIZING_KEY]) / 2 - borderWidth,
        },
        /** Class name of the root element when the component is disabled. */
        rootDisabled: (_e = {
                color: hawkins.textColor.textColorXlight,
                cursor: 'not-allowed'
            },
            _e[iconChildrenSelector] = { color: hawkins.icon.fillXlight },
            _e),
        /** Class name of the root element when the component is not disabled. */
        rootEnabled: (_f = {},
            _f[hawkinsUtils.getHoveredElementSelector('&')] = { backgroundColor: hawkins.state.backgroundColorHover },
            _f),
        /** Class name of the root element in a color variant (`"icon-color"` or `"tertiary-color"`). */
        rootColor: (_g = {
                color: hawkins.textColor.textColorInteractiveRegular
            },
            _g[iconChildrenSelector] = { color: hawkins.icon.fillInteractiveRegular },
            _g),
        /** Class name of the root element in a color variant when the component is disabled. */
        rootColorDisabled: (_h = {
                color: hawkins.textColor.textColorInteractiveXlight
            },
            _h[iconChildrenSelector] = { color: hawkins.icon.fillInteractiveXlight },
            _h),
        /** Class name of the root element in the destructive variant. */
        rootDestructive: (_j = {
                backgroundColor: hawkins.destructive.backgroundColorRegular,
                color: hawkins.textColor.textColorReverseRegular,
                '&[aria-expanded="true"]': { backgroundColor: hawkins.destructive.backgroundColorHover }
            },
            _j["&.".concat(HawkinsGlobalClass.FOCUSABLE)] = (_k = {},
                _k[hawkinsUtils.getFocusRingedElementSelector('&', '::after')] = {
                    borderColor: hawkins.destructive.backgroundColorRegular,
                },
                _k),
            _j[iconChildrenSelector] = { color: hawkins.textColor.textColorReverseRegular },
            _j),
        /** Class name of the root element in the destructive variant when the component is disabled. */
        rootDestructiveDisabled: (_l = {
                backgroundColor: hawkins.destructive.backgroundColorDisabled,
                color: hawkins.textColor.textColorReverseXlight
            },
            _l[iconChildrenSelector] = { color: hawkins.icon.fillReverseXlight },
            _l),
        /** Class name of the root element in the destructive variant when the component is not disabled. */
        rootDestructiveEnabled: (_m = {},
            _m[hawkinsUtils.getHoveredElementSelector('&')] = { backgroundColor: hawkins.destructive.backgroundColorHover },
            _m),
        /** Class name of the root element when the no-wrap feature is enabled. */
        rootNoWrap: {
            whiteSpace: 'nowrap',
        },
        /** Class name of the root element in the primary variant. */
        rootPrimary: (_o = {
                backgroundColor: hawkins.interactive.backgroundColorRegular,
                color: hawkins.persistent.textColorWhiteRegular,
                '&[aria-expanded="true"]': { backgroundColor: hawkins.interactive.backgroundColorHover }
            },
            _o[iconChildrenSelector] = { color: hawkins.persistent.textColorWhiteRegular },
            _o),
        /** Class name of the root element in the primary variant when the component is disabled. */
        rootPrimaryDisabled: (_p = {
                backgroundColor: hawkins.interactive.backgroundColorDisabled,
                color: hawkins.persistent.textColorWhiteXlight
            },
            _p[iconChildrenSelector] = { color: hawkins.persistent.textColorWhiteXlight },
            _p),
        /** Class name of the root element in the primary variant when the component is not disabled. */
        rootPrimaryEnabled: (_q = {},
            _q[hawkinsUtils.getHoveredElementSelector('&')] = { backgroundColor: hawkins.interactive.backgroundColorHover },
            _q),
        /** Class name of the root element in a reverse variant (`"icon-reverse"` or `"tertiary-reverse"`). */
        rootReverse: (_r = {
                color: hawkins.textColor.textColorReverseRegular
            },
            _r[iconChildrenSelector] = { color: hawkins.icon.fillReverseRegular },
            _r),
        /** Class name of the root element in a reverse variant when the component is disabled. */
        rootReverseDisabled: (_s = {
                color: hawkins.textColor.textColorReverseXlight
            },
            _s[iconChildrenSelector] = { color: hawkins.icon.fillReverseXlight },
            _s),
        /** Class name of the root element in the secondary variant. */
        rootSecondary: {
            borderColor: hawkins.ui.borderColorLight,
        },
        /** Class name of the root element in the secondary variant when the component is disabled. */
        rootSecondaryDisabled: {
            borderColor: hawkins.ui.borderColorXlight,
        },
    });
}
export var useButtonStyles = makeStyles(buttonStyles, { name: 'Hawkins-Button' });
